/**
 * licenseToken.js
 * ─────────────────────────────────────────────────────────────
 * Custom date encryptor / decryptor for URL-based license gating.
 *
 * Algorithm (3 layers):
 *   Encrypt:  XOR with rolling key → Reverse bytes → URL-safe Base64
 *   Decrypt:  Reverse URL-safe Base64 → Un-reverse bytes → XOR with rolling key
 *
 * ⚠️  The SECRET_KEY must be identical in this file AND in the
 *     standalone admin HTML tool (token-generator.html).
 * ─────────────────────────────────────────────────────────────
 */

const SECRET_KEY = "BWC@N3tw0rk!D14gr4m";

/**
 * Encrypts a date string into a URL-safe token.
 * @param {string} dateStr - ISO date string, e.g. "2026-12-31"
 * @returns {string} Encrypted token safe for use in a URL query param
 * @deprecated Use encryptToken(baseUrl, dateStr) for new tokens.
 */
export function encryptDate(dateStr) {
  // Layer 1: XOR each character with the rolling secret key
  const xored = [];
  for (let i = 0; i < dateStr.length; i++) {
    xored.push(
      dateStr.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length)
    );
  }

  // Layer 2: Reverse the byte array
  xored.reverse();

  // Layer 3: Base64 encode, then make URL-safe via char substitution
  const binary = xored.map((b) => String.fromCharCode(b)).join("");
  const b64 = btoa(binary);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, ".");
}

/**
 * Decrypts a URL token back into a date string.
 * @param {string} token - The encrypted token from the URL ?key= param
 * @returns {string|null} ISO date string "YYYY-MM-DD", or null if invalid/tampered
 * @deprecated Use decryptToken(token) for new tokens.
 */
export function decryptDate(token) {
  try {
    // Reverse URL-safe substitution
    const b64 = token.replace(/-/g, "+").replace(/_/g, "/").replace(/\./g, "=");

    // Base64 decode
    const binary = atob(b64);
    const bytes = Array.from(binary).map((c) => c.charCodeAt(0));

    // Un-reverse the byte array
    bytes.reverse();

    // XOR with the same rolling key to recover the original string
    const chars = bytes.map((b, i) =>
      String.fromCharCode(b ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length))
    );

    const result = chars.join("");

    // Validate the result looks like a real date (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(result)) {
      return result;
    }

    return null; // Tampered or wrong key
  } catch {
    return null; // Malformed base64 or other error
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  NEW: Combined Base URL + Date token
//  Payload format (before encryption): "<baseUrl>|<YYYY-MM-DD>"
//  The pipe | separator is safe because URLs and ISO dates never contain it.
// ─────────────────────────────────────────────────────────────────────────────

const SEPARATOR = "|";

/**
 * Encrypts a base URL + expiry date into a single URL-safe token.
 *
 * @param {string} baseUrl  - The canonical widget deployment URL,
 *                            e.g. "https://bwc-135.brainwaveconsulting.co.in/Pert_Chart_Build/"
 * @param {string} dateStr  - ISO expiry date, e.g. "2026-12-31"
 * @returns {string} Encrypted token
 */
export function encryptToken(baseUrl, dateStr) {
  const payload = `${baseUrl}${SEPARATOR}${dateStr}`;

  // Layer 1: XOR with rolling key
  const xored = [];
  for (let i = 0; i < payload.length; i++) {
    xored.push(
      payload.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length)
    );
  }

  // Layer 2: Reverse bytes
  xored.reverse();

  // Layer 3: URL-safe Base64
  const binary = xored.map((b) => String.fromCharCode(b)).join("");
  const b64 = btoa(binary);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, ".");
}

/**
 * Decrypts a combined token back into its baseUrl and expiry date.
 *
 * @param {string} token - The encrypted token from the URL
 * @returns {{ baseUrl: string, expiryDate: string } | null}
 *          Parsed payload object, or null if invalid / tampered
 */
export function decryptToken(token) {
  try {
    // Reverse URL-safe substitution
    const b64 = token.replace(/-/g, "+").replace(/_/g, "/").replace(/\./g, "=");

    // Base64 decode
    const binary = atob(b64);
    const bytes = Array.from(binary).map((c) => c.charCodeAt(0));

    // Un-reverse bytes
    bytes.reverse();

    // XOR with rolling key
    const chars = bytes.map((b, i) =>
      String.fromCharCode(b ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length))
    );

    const result = chars.join("");

    // Split on the separator
    const separatorIndex = result.lastIndexOf(SEPARATOR);
    if (separatorIndex === -1) return null;

    const baseUrl    = result.slice(0, separatorIndex);
    const expiryDate = result.slice(separatorIndex + 1);

    // Validate date portion looks like YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) return null;

    // Validate base URL is a plausible URL
    if (!baseUrl.startsWith("http")) return null;

    return { baseUrl, expiryDate };
  } catch {
    return null; // Malformed base64 or other error
  }
}
